import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Plus, MessageSquare, Workflow, Settings } from 'lucide-react';
import { PageTransition } from '@/components/motion';
import Card from '@/components/UI/Card';
import Badge from '@/components/UI/Badge';
import BrokerSettingsModal from '@/components/Apollo/BrokerSettingsModal';
import { brokersAPI } from '@/services/apolloApi';
import { useSession } from '@/lib/authClient';

const STATUS_CONFIG = {
  idle: { variant: 'neutral', label: 'Idle' },
  analyzing: { variant: 'info', label: 'Analyzing' },
  trading: { variant: 'success', label: 'Trading' },
  paused: { variant: 'warning', label: 'Paused' },
  error: { variant: 'danger', label: 'Error' },
};

function BrokerList() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBroker, setEditBroker] = useState(null);

  useEffect(() => {
    if (!session?.user?.id) return;
    setLoading(true);
    brokersAPI.getAll()
      .then((all) => setBrokers(all.filter((b) => !b.config?.system)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [session?.user?.id]);

  if (loading) {
    return (
      <PageTransition className="p-6">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-foreground">Brokers</h1>
        <button
          onClick={() => navigate('/apollo/brokers/new')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <Plus className="w-4 h-4" />
          New Broker
        </button>
      </div>

      {brokers.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <Bot className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground mb-1">No brokers yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create an AI broker to manage your trading strategies autonomously.
            </p>
            <button
              onClick={() => navigate('/apollo/brokers/new')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Plus className="w-4 h-4" />
              Create Your First Broker
            </button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brokers.map((broker) => {
            const statusCfg = STATUS_CONFIG[broker.status] || STATUS_CONFIG.idle;
            return (
              <Card
                key={broker.id}
                className="p-5 cursor-pointer hover:bg-white/[0.02] transition-colors"
                onClick={() => navigate(`/apollo/brokers/${broker.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
                      <Bot className="w-4.5 h-4.5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground text-sm">{broker.name}</h3>
                      <span className="text-xs text-muted-foreground capitalize">{broker.autonomy}</span>
                    </div>
                  </div>
                  <Badge variant={statusCfg.variant} className="text-[10px]">{statusCfg.label}</Badge>
                </div>

                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  {broker.config?.tradingMode && (
                    <Badge variant={broker.config.tradingMode === 'live' ? 'danger' : 'info'} className="text-[10px]">
                      {broker.config.tradingMode}
                    </Badge>
                  )}
                  {broker.config?.assetFocus && (
                    <span className="capitalize">{broker.config.assetFocus}</span>
                  )}
                  <span className="ml-auto">{new Date(broker.created_at).toLocaleDateString()}</span>
                </div>

                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/apollo/chat/${broker.id}`); }}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    Chat
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/apollo/workflow/${broker.id}`); }}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors"
                  >
                    <Workflow className="w-3.5 h-3.5" />
                    Workflow
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditBroker(broker); }}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors ml-auto"
                  >
                    <Settings className="w-3.5 h-3.5" />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <BrokerSettingsModal
        open={!!editBroker}
        onClose={() => setEditBroker(null)}
        broker={editBroker}
        onSave={async (data) => {
          const updated = await brokersAPI.update(editBroker.id, data);
          setBrokers((prev) => prev.map((b) => b.id === updated.id ? updated : b));
        }}
      />
    </PageTransition>
  );
}

export default BrokerList;
